'use client';

// app-progress.tsx
//
// Public Progress wrapper. Adds an `indicatorClassName` prop so consumers can
// style the indicator fill (gradient, brand tint, etc.) without touching the
// underlying shadcn primitive — FQ-111 forbids edits to `progress.tsx`.
//
// The shadcn primitive does not expose the indicator className, so we render
// the Radix Root + Indicator directly here. This keeps customisations in the
// wrapper layer (the public surface) while leaving `progress.tsx` byte-stock.
import { c as _c } from "react/compiler-runtime";
import { Progress as ProgressPrimitive } from 'radix-ui';
import type * as React from 'react';
import { cn } from '@/lib/ui/utils';
export interface ProgressProps extends React.ComponentProps<typeof ProgressPrimitive.Root> {
  /** className applied to the inner indicator element. */
  indicatorClassName?: string | undefined;
  /** Inline styles merged onto the indicator (after transform). Use for dynamic fills, e.g. outcome ramp `backgroundColor`. */
  indicatorStyle?: React.CSSProperties | undefined;
}
export function Progress(t0) {
  const $ = _c(23);
  let className;
  let indicatorClassName;
  let indicatorStyle;
  let props;
  let t1;
  let value;
  if ($[0] !== t0) {
    ({
      className,
      indicatorClassName,
      indicatorStyle,
      value,
      max: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = indicatorClassName;
    $[3] = indicatorStyle;
    $[4] = props;
    $[5] = t1;
    $[6] = value;
  } else {
    className = $[1];
    indicatorClassName = $[2];
    indicatorStyle = $[3];
    props = $[4];
    t1 = $[5];
    value = $[6];
  }
  const max = t1 === undefined ? 100 : t1;
  let t2;
  if ($[7] !== className) {
    t2 = cn("relative h-2 w-full overflow-hidden rounded-full bg-foreground-primary/20", className);
    $[7] = className;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== indicatorClassName) {
    t3 = cn("h-full w-full flex-1 bg-foreground-primary transition-all", indicatorClassName);
    $[9] = indicatorClassName;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const t4 = `translateX(-${100 - (value ?? 0) / max * 100}%)`;
  let t5;
  if ($[11] !== indicatorStyle || $[12] !== t4) {
    t5 = {
      transform: t4,
      ...indicatorStyle
    };
    $[11] = indicatorStyle;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== t3 || $[15] !== t5) {
    t6 = <ProgressPrimitive.Indicator data-slot="progress-indicator" className={t3} style={t5} />;
    $[14] = t3;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== max || $[18] !== props || $[19] !== t2 || $[20] !== t6 || $[21] !== value) {
    t7 = <ProgressPrimitive.Root data-slot="progress" className={t2} value={value} max={max} {...props}>{t6}</ProgressPrimitive.Root>;
    $[17] = max;
    $[18] = props;
    $[19] = t2;
    $[20] = t6;
    $[21] = value;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  return t7;
}